import { useEffect, useMemo, useState } from 'react'
import type { CommandSummary } from '@/types'
import { formatKstDate } from '@/utils/datetime'
import Pagination from './Pagination'

interface Props {
  commands: CommandSummary[]
}

const PAGE_SIZE = 10

const STATUS_STYLE: Record<string, { bg: string; text: string }> = {
  queued:     { bg: '#f5f5f5', text: '#555' },
  dispatched: { bg: '#e3f2fd', text: '#1565c0' },
  accepted:   { bg: '#e8f5e9', text: '#2e7d32' },
  running:    { bg: '#e8f5e9', text: '#1b5e20' },
  succeeded:  { bg: '#e8f5e9', text: '#2e7d32' },
  failed:     { bg: '#ffebee', text: '#c62828' },
  cancelled:  { bg: '#fff8e1', text: '#f57f17' },
  timed_out:  { bg: '#fce4ec', text: '#880e4f' },
}

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLE[status] ?? { bg: '#f0f0f0', text: '#666' }
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '2px 8px',
        borderRadius: 10,
        fontSize: 11,
        fontWeight: 600,
        background: s.bg,
        color: s.text,
        whiteSpace: 'nowrap',
      }}
    >
      {status}
    </span>
  )
}

function relativeTime(ts: string): string {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000)
  if (diff < 60) return `${diff}s ago`
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`
  return formatKstDate(ts, '—')
}

function commandTypeLabel(type: string): string {
  return type.replace('.', ' ').replace(/_/g, ' ')
}

export default function CommandHistory({ commands }: Props) {
  const [page, setPage] = useState(1)
  const totalPages = Math.max(1, Math.ceil(commands.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  const visibleCommands = useMemo(
    () => commands.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [commands, safePage],
  )

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages))
  }, [totalPages])

  if (commands.length === 0) {
    return (
      <div style={cardStyle}>
        <div style={cardTitleStyle}>Command History</div>
        <p style={{ color: '#aaa', fontSize: 13, margin: 0 }}>No commands yet</p>
      </div>
    )
  }

  return (
    <div style={cardStyle}>
      <div style={cardTitleStyle}>Command History</div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {['Type', 'Camera', 'Status', 'Requested by', 'Reason', 'Created'].map((h) => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visibleCommands.map((cmd, i) => (
              <tr key={cmd.id} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                <td style={tdStyle}>
                  <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {commandTypeLabel(cmd.type)}
                  </span>
                </td>
                <td style={{ ...tdStyle, color: '#555', fontFamily: 'monospace', fontSize: 12 }}>
                  {cmd.camera_key ?? '—'}
                </td>
                <td style={tdStyle}>
                  <StatusBadge status={cmd.status} />
                </td>
                <td style={{ ...tdStyle, color: '#666' }}>{cmd.requested_by ?? '—'}</td>
                <td style={{ ...tdStyle, color: '#666', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {cmd.reason ?? '—'}
                </td>
                <td style={{ ...tdStyle, color: '#999', whiteSpace: 'nowrap' }}>
                  {relativeTime(cmd.created_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <Pagination
          page={safePage}
          pageSize={PAGE_SIZE}
          total={commands.length}
          onPageChange={setPage}
          itemLabel="commands"
        />
      )}
    </div>
  )
}

const cardStyle: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e0e0e0',
  borderRadius: 6,
  padding: '14px 16px',
  overflowX: 'auto',
}

const cardTitleStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  color: '#333',
  marginBottom: 12,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
}

const thStyle: React.CSSProperties = {
  padding: '6px 10px',
  textAlign: 'left',
  fontSize: 11,
  fontWeight: 700,
  color: '#888',
  background: '#f8f8f8',
  borderBottom: '1px solid #eee',
  whiteSpace: 'nowrap',
}

const tdStyle: React.CSSProperties = {
  padding: '7px 10px',
  borderBottom: '1px solid #f0f0f0',
  verticalAlign: 'middle',
}
